import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Button from "../components/ui/Button";
import Footer from "../components/Footer";

const PART1 = "Stay online. ";
const PART2 = "Stay on time.";
const FULL_TEXT = PART1 + PART2;

export default function Landing() {
  const { token, isRefreshing } = useAuth();

  // Skip animation for users who prefer reduced motion
  const skipAnimation =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [typedCount, setTypedCount] = useState(
    skipAnimation ? FULL_TEXT.length : 0
  );
  const [showCursor, setShowCursor] = useState(!skipAnimation);

  useEffect(() => {
    if (typedCount < FULL_TEXT.length) {
      // 2-second pause after "Stay online. " before typing the second part
      const delay = typedCount === PART1.length ? 2000 : 100;
      const t = setTimeout(() => setTypedCount((c) => c + 1), delay);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => setShowCursor(false), 700);
      return () => clearTimeout(t);
    }
  }, [typedCount]);
  
  // If user is logged in, redirect to dashboard
  if (token && !isRefreshing) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Show loading while checking auth status
  if (isRefreshing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
        <div className="flex items-center justify-between">
          {/* Logo - Adjusted for mobile */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
            <img 
              src="/logo-icon.png" 
              alt="SiteScope" 
              className="w-8 h-8 sm:w-12 sm:h-12 md:w-14 md:h-14 transition-transform group-hover:scale-105" 
            />
            <span className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
              SiteScope
            </span>
          </Link>
          
          {/* Navigation - Clean mobile design */}
          <div className="flex items-center gap-1 sm:gap-4">
            {/* Pricing as text link on mobile, button on desktop */}
            <Link 
              to="/pricing" 
              className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors hidden sm:block"
            >
              Pricing
            </Link>
            
            {/* Sign in - Ghost button */}
            <Link to="/login" className="text-sm sm:text-base px-3 sm:px-4 py-2 font-semibold text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded-xl">
              Sign in
            </Link>
            
            {/* Start Free - Primary CTA */}
            <Button as={Link} to="/register" size="sm" light className="text-sm sm:text-base px-3 sm:px-6 py-2 shadow-lg font-semibold whitespace-nowrap">
              Start Free<span className="hidden sm:inline"> (3 sites)</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Improved mobile spacing */}
      <main className="max-w-7xl mx-auto px-5 sm:px-6 py-6 sm:py-10 lg:py-14">
        <div className="text-center max-w-4xl mx-auto">
          {/* Hero Title - Better mobile typography */}
          <h1 className="tracking-tight text-gray-900 dark:text-gray-100 mb-5 sm:mb-6">
            {/* Screen-reader: announce full text once */}
            <span className="sr-only">{FULL_TEXT}</span>
            <span
              aria-hidden="true"
              className="block text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.1] sm:leading-tight"
            >
              {FULL_TEXT.slice(0, Math.min(typedCount, PART1.length))}
              {typedCount > PART1.length && (
                <span className="dark:text-blue-400">
                  {FULL_TEXT.slice(PART1.length, typedCount)}
                </span>
              )}
              {showCursor && (
                <span
                  aria-hidden="true"
                  className="inline-block w-[3px] h-[0.85em] bg-current align-middle ml-1 animate-pulse"
                />
              )}
            </span>
          </h1>
          
          {/* Subtitle - Improved mobile readability */}
          <p className="text-lg sm:text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 sm:mb-10 leading-relaxed px-1 sm:px-4">
            24/7 monitoring for uptime, SSL expiry, and critical pages — with instant alerts on email and mobile (PWA).
          </p>
          
          {/* CTA Buttons - Better mobile touch targets */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 mb-6 sm:mb-16 px-2">
            <Button as={Link} to="/register" size="xl" light className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold shadow-xl hover:shadow-2xl">
              Start Free — Monitor 3 Sites
            </Button>
            <Button as="a" href="#features" variant="outline" size="xl" light className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold border-2">
              See How It Works
            </Button>
          </div>

          {/* Features Grid - Better mobile layout */}
          <div id="features" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8 max-w-5xl mx-auto mb-12 sm:mb-16 lg:mb-20">
            <div className="bg-white dark:bg-gray-800 p-6 sm:p-7 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl hover:scale-105 transition-all">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <span className="text-3xl sm:text-4xl">⚡</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">Protect Your Reputation</h3>
              <p className="text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                Know the moment a client site goes down — before they call you.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 sm:p-7 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl hover:scale-105 transition-all">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 dark:bg-green-900 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <span className="text-3xl sm:text-4xl">📊</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">One Dashboard for All Client Sites</h3>
              <p className="text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                Monitor every website you manage in one simple view.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 sm:p-7 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl hover:scale-105 transition-all sm:col-span-2 lg:col-span-1">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-purple-100 dark:bg-purple-900 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <span className="text-3xl sm:text-4xl">🚀</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">Alerts That Help You Act Fast</h3>
              <p className="text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                Get instant notifications when a site goes down or comes back online.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Stats Section - Mobile optimized */}
      <section className="bg-white dark:bg-gray-800 py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">
              Monitor every site you manage in one place.
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400">
              Built by a developer who manages client sites — focused on reliable monitoring and fast alerts.
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 text-center">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 sm:p-6 rounded-xl">
              <div className="text-base sm:text-xl lg:text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">Monitor 24/7</div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">Background checks</div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 p-4 sm:p-6 rounded-xl">
              <div className="text-base sm:text-xl lg:text-2xl font-bold text-green-600 dark:text-green-400 mb-1">Instant alerts</div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">Email notifications</div>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 sm:p-6 rounded-xl">
              <div className="text-base sm:text-xl lg:text-2xl font-bold text-purple-600 dark:text-purple-400 mb-1">Free: 3 sites</div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">Client sites included</div>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 sm:p-6 rounded-xl">
              <div className="text-base sm:text-xl lg:text-2xl font-bold text-orange-600 dark:text-orange-400 mb-1">CMS & Apps</div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">WordPress • Shopify • Webflow • Custom builds</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section - Mobile friendly */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">
              How it works
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400">
              Get started in minutes with our simple process
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10">
            <div className="text-center bg-white dark:bg-gray-800 p-6 sm:p-8 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-5 sm:mb-6">
                <span className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400">1</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">Add Website</h3>
              <p className="text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                Simply enter your client site URLs and we'll start monitoring immediately.
              </p>
            </div>
            
            <div className="text-center bg-white dark:bg-gray-800 p-6 sm:p-8 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-5 sm:mb-6">
                <span className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400">2</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">Monitor 24/7</h3>
              <p className="text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                Our system checks your client sites regularly and tracks performance metrics.
              </p>
            </div>
            
            <div className="text-center bg-white dark:bg-gray-800 p-6 sm:p-8 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-5 sm:mb-6">
                <span className="text-2xl sm:text-3xl font-bold text-purple-600 dark:text-purple-400">3</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">Get alerts instantly</h3>
              <p className="text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                Receive instant notifications when your client sites go down or come back up.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section - Below How It Works */}
      <section className="bg-white dark:bg-gray-800 py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="text-center mb-6 sm:mb-8 px-2">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">
              See How SiteScope Protects Your Client Sites
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400">
              A quick walkthrough of how freelancers monitor and manage client websites.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg sm:shadow-xl lg:shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden max-w-4xl mx-auto">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/EOgBrXsO1Ec?rel=0&modestbranding=1"
                title="SiteScope - 24/7 Website Monitoring"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          <div className="text-center mt-4 sm:mt-6 px-4">
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
              ⚡ Instant alerts • 🔒 SSL monitoring • 📱 Mobile-friendly dashboard • 🧩 Built for all types of website
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section - Mobile optimized */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 py-12 sm:py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            Ready to protect your websites?
          </h2>
          <p className="text-base sm:text-xl text-blue-50 mb-6 sm:mb-8 leading-relaxed">
            Join companies who protect their reputation with instant alerts.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
            <Button as={Link} to="/register" variant="secondary" size="xl" light className="w-full sm:w-auto px-8 py-4 text-lg font-semibold !bg-white !text-blue-600 hover:!bg-gray-100 shadow-xl">
              🚀 Start Free Today
            </Button>
            <Button as={Link} to="/pricing" variant="outline" size="xl" light className="w-full sm:w-auto px-8 py-4 text-lg font-semibold !border-2 !border-white !text-white !bg-transparent hover:!bg-white hover:!text-blue-600">
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}


